import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { database } from '../lib/firebase.ts';
import { GameRoom as GameRoomType, Player, Guess } from '../types/game.ts';
import { validateGuess, generateRoomCode, isWordValid } from '../utils/wordle.ts';
import { GameService } from '../services/gameService.ts';
import WordleGame from './WordleGame.tsx';
import PlayerList from './PlayerList.tsx';

const GameRoom: React.FC = () => {
    const { roomId } = useParams<{ roomId: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [room, setRoom] = useState<GameRoomType | null>(null);
    const [currentPlayer, setCurrentPlayer] = useState<Player | null>(null);
    const [currentGuess, setCurrentGuess] = useState('');
    const [hostWord, setHostWord] = useState('');
    const [hostWordValid, setHostWordValid] = useState<boolean | null>(null);
    const [wordLength, setWordLength] = useState(5);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isHost, setIsHost] = useState(false);
    const [isStreamerMode, setIsStreamerMode] = useState(false);
    const [pendingGuess, setPendingGuess] = useState<Guess | null>(null);
    const lastConfirmedCountRef = useRef<number>(0);

    // Yayıncı modu kontrolleri
    const [showRoomCode, setShowRoomCode] = useState(false);
    const [showTargetWord, setShowTargetWord] = useState(false);
    const [showPlayerGuesses, setShowPlayerGuesses] = useState(false);

    // Firebase ile oda yönetimi
    useEffect(() => {
        const initializeRoom = async () => {
            try {
                console.log('Oda başlatılıyor...', { roomId, searchParams: searchParams.toString() });
                setIsLoading(true);
                const playerName = searchParams.get('player') || searchParams.get('host') || 'Oyuncu';
                const hostStatus = !!searchParams.get('host');
                const streamerMode = !!searchParams.get('streamer');
                setIsHost(hostStatus);
                setIsStreamerMode(streamerMode);
                console.log('Player bilgileri:', { playerName, hostStatus, streamerMode });

                // Session persistence için localStorage kontrolü
                const sessionKey = `wordle_session_${roomId}`;
                const existingSession = localStorage.getItem(sessionKey);

                let newPlayer: Player;

                if (existingSession && hostStatus) {
                    // Host için: mevcut session'ı kullan
                    const sessionData = JSON.parse(existingSession);
                    newPlayer = sessionData.player;
                    const existingRoom = await GameService.getRoom(roomId!);
                    if (existingRoom && existingRoom.hostId === newPlayer.id) {
                        // Host gerçekten bu odanın host'u
                        setRoom(existingRoom);
                        setCurrentPlayer(newPlayer);
                        setIsLoading(false);
                        return;
                    } else {
                        // Host değil veya oda bulunamadı, yeni host olarak oda oluştur
                        console.log('Host session bulunamadı, yeni oda oluşturuluyor');
                    }
                } else if (existingSession && !hostStatus) {
                    // Oyuncu için: oda var mı ve oyuncu odada mı kontrol et
                    const existingRoom = await GameService.getRoom(roomId!);
                    if (existingRoom) {
                        const sessionData = JSON.parse(existingSession);
                        newPlayer = sessionData.player;

                        // Oyuncu hala odada mı kontrol et
                        const playerInRoom = existingRoom.players?.find(p => p.id === newPlayer.id);
                        if (playerInRoom) {
                            // Oyuncu odada, devam et
                            setRoom(existingRoom);
                            setCurrentPlayer(newPlayer);
                            setIsLoading(false);
                            return;
                        } else {
                            // Oyuncu odada değil, isim girişi sayfasına yönlendir
                            setIsLoading(false);
                            navigate(`/join?room=${roomId}`);
                            return;
                        }
                    } else {
                        // Oda yok, isim girişi sayfasına yönlendir
                        setIsLoading(false);
                        navigate(`/join?room=${roomId}`);
                        return;
                    }
                }

                // Yeni session oluştur
                newPlayer = {
                    id: Math.random().toString(36).substring(2, 15),
                    name: playerName,
                    guesses: [],
                    isGameComplete: false,
                    hasWon: false,
                    isHost: hostStatus
                };

                if (hostStatus) {
                    // Oda oluştur
                    console.log('Host oda oluşturuyor...');
                    const roomCode = roomId || generateRoomCode();
                    console.log('Oda kodu:', roomCode);
                    const newRoom = await GameService.createRoom(roomCode, newPlayer);
                    console.log('Oda oluşturuldu:', newRoom);
                    setRoom(newRoom);

                    // Host session'ını kaydet
                    localStorage.setItem(sessionKey, JSON.stringify({
                        player: newPlayer,
                        roomCode: roomCode
                    }));
                } else {
                    // Odaya katıl
                    console.log('Oyuncu odaya katılıyor...', roomId);
                    const existingRoom = await GameService.joinRoom(roomId!, newPlayer);
                    console.log('Odaya katıldı:', existingRoom);
                    setRoom(existingRoom);

                    // Oyuncu session'ını kaydet
                    localStorage.setItem(sessionKey, JSON.stringify({
                        player: newPlayer,
                        roomCode: roomId
                    }));
                }

                setCurrentPlayer(newPlayer);
                lastConfirmedCountRef.current = newPlayer.guesses?.length || 0;
                console.log('Oda başlatma tamamlandı');
                setIsLoading(false);
            } catch (error) {
                console.error('Oda başlatma hatası:', error);
                setError('Oda oluşturulurken bir hata oluştu');
                setIsLoading(false);
            }
        };

        initializeRoom();
    }, [roomId, searchParams, navigate]);

    // Sayfa kapatıldığında host ise odayı sil
    useEffect(() => {
        const handleBeforeUnload = async () => {
            if (isHost && room) {
                try {
                    await GameService.notifyRoomDeleted(room.code);
                    await GameService.deleteRoom(room.code);
                } catch (error) {
                    console.error('Oda silme hatası:', error);
                }
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isHost, room]);

    // Oyuncular için oda silindi bildirimini dinle
    useEffect(() => {
        if (!roomId || isHost) return;

        const notificationRef = ref(database, `notifications/${roomId}`);
        const unsubscribe = onValue(notificationRef, (snapshot) => {
            if (snapshot.exists()) {
                const notification = snapshot.val();
                if (notification.type === 'room_deleted') {
                    alert(notification.message);
                    // Session'ı temizle ve ana ekrana yönlendir
                    const sessionKey = `wordle_session_${roomId}`;
                    localStorage.removeItem(sessionKey);
                    navigate('/');
                }
            }
        });

        return () => {
            unsubscribe();
        };
    }, [roomId, isHost, navigate]);

    // Real-time oda güncellemelerini dinle
    useEffect(() => {
        if (!roomId) return;

        const subscription = GameService.subscribeToRoom(roomId, (updatedRoom: GameRoomType) => {
            console.log('Room updated:', updatedRoom); // Debug için
            setRoom(updatedRoom);

            // Mevcut oyuncuyu güncelle
            const updatedPlayer = updatedRoom.players?.find(p => p.id === currentPlayer?.id);
            if (updatedPlayer) {
                setCurrentPlayer(updatedPlayer);
                // Pending tahmini onay geldiğinde temizle (sadece uzunluk artışına bak)
                const confirmedCount = updatedPlayer.guesses?.length || 0;
                if (confirmedCount > (lastConfirmedCountRef.current || 0)) {
                    lastConfirmedCountRef.current = confirmedCount;
                    // Stale state sorunlarını önlemek için koşulsuz temizle
                    setPendingGuess(null);
                }
            }
        });

        return () => {
            GameService.unsubscribe(subscription);
        };
    }, [roomId, currentPlayer?.id]);

    // Oyun durumu değişince pending'i temizle (host yeniden başlatırsa tüm istemciler)
    useEffect(() => {
        if (!room) return;
        if (room.gameStatus === 'waiting') {
            setPendingGuess(null);
            lastConfirmedCountRef.current = 0;
            setCurrentGuess('');
        }
        // Yeni hedef kelime belirlendiğinde de sayaçları toparla
        if (room.gameStatus === 'playing') {
            const confirmed = currentPlayer?.guesses?.length || 0;
            lastConfirmedCountRef.current = confirmed;
        }
    }, [room?.gameStatus, room?.targetWord]);

    // Host word validation
    useEffect(() => {
        const validateHostWord = async () => {
            if (hostWord.length === wordLength) {
                const isValid = await isWordValid(hostWord, wordLength);
                setHostWordValid(isValid);
            } else {
                setHostWordValid(null);
            }
        };

        validateHostWord();
    }, [hostWord, wordLength]);

    // Kelime uzunluğu değiştiğinde hostWord'ü temizle
    useEffect(() => {
        setHostWord('');
        setHostWordValid(null);
    }, [wordLength]);

    const handleGuessChange = (guess: string) => {
        // Kullanıcı yazmaya başlarken, eğer pending ve onaylanmış sayım arttıysa pending'i temizle
        if (pendingGuess && (currentPlayer?.guesses?.length || 0) >= (lastConfirmedCountRef.current || 0)) {
            setPendingGuess(null);
        }
        setCurrentGuess(guess);
    };

    const handleGuessSubmit = async () => {
        if (!room || !currentPlayer || currentGuess.length !== room.targetWord.length) return;
        if (pendingGuess) return; // Çifte gönderimi engelle

        try {
            const tiles = validateGuess(currentGuess, room.targetWord);
            const newGuess: Guess = {
                tiles,
                isComplete: true
            };

            const hasWon = tiles.every(tile => tile.state === 'correct');
            const isGameComplete = hasWon || (currentPlayer.guesses?.length || 0) + 1 >= room.maxGuesses;

            const updatedGuesses = [...(currentPlayer.guesses || []), newGuess];

            // UI'da pending olarak göster
            setPendingGuess(newGuess);
            setCurrentGuess('');

            // Firebase'e transaction ile ekle (eşzamanlı gönderimlerde kayıp yok)
            await GameService.appendPlayerGuessTransactional(room.code, currentPlayer.id, newGuess, {
                isGameComplete,
                hasWon
            });

            // Eğer oyun bitti ise oda durumunu güncelle
            if (isGameComplete) {
                const allPlayersComplete = room.players?.every(p =>
                    p.id === currentPlayer.id ? isGameComplete : p.isGameComplete
                ) || false;
                if (allPlayersComplete) {
                    await GameService.updateRoom(room.code, { gameStatus: 'finished' });
                }
            }
        } catch (error) {
            console.error('Tahmin kaydetme hatası:', error);
            setError('Tahmin kaydedilirken bir hata oluştu');
            // Hata durumunda pending'i kaldır ve currentGuess'i geri getir (isteğe bağlı)
            const pgAny = pendingGuess as any;
            if (pgAny && pgAny.tiles) {
                setCurrentGuess(pgAny.tiles.map((t: any) => t.letter).join(''));
                setPendingGuess(null);
            }
        }
    };

    const copyRoomCode = () => {
        if (room) {
            navigator.clipboard.writeText(room.code);
            alert(`Oda kodu kopyalandı: ${room.code}`);
        }
    };

    const copyShareLink = () => {
        if (room) {
            const shareLink = `https://grup-wordle-v2.vercel.app/join?room=${room.code}`;
            navigator.clipboard.writeText(shareLink);
            alert('Paylaşım linki kopyalandı!');
        }
    };


    const submitWord = async () => {
        if (room && hostWord.length === wordLength && hostWordValid) {
            try {
                await GameService.submitWord(room.code, hostWord);
                setHostWord('');
                setHostWordValid(null);
            } catch (error) {
                console.error('Kelime gönderme hatası:', error);
                setError('Kelime gönderilirken bir hata oluştu');
            }
        }
    };

    const restartGame = async () => {
        if (!room || !isHost) return;

        try {
            // Oyunu sıfırla - oyuncuları odada tut
            await GameService.updateRoom(room.code, {
                gameStatus: 'waiting',
                targetWord: '',
                wordSubmitted: false,
                updatedAt: new Date().toISOString()
            });

            // Tüm oyuncuların oyun durumunu sıfırla
            if (room.players) {
                for (const player of room.players) {
                    await GameService.updatePlayer(room.code, player.id, {
                        guesses: [],
                        isGameComplete: false,
                        hasWon: false
                    });
                }
            }

            setHostWord('');
            setHostWordValid(null);
        } catch (error) {
            console.error('Oyun yeniden başlatma hatası:', error);
            setError('Oyun yeniden başlatılırken bir hata oluştu');
        }
    };


    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-white text-xl">Yükleniyor...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-red-400 text-xl">{error}</div>
            </div>
        );
    }

    if (!room || !currentPlayer) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-white text-xl">Oda yükleniyor...</div>
            </div>
        );
    }

    const canSubmitWord = isHost && room.gameStatus === 'waiting' && !room.wordSubmitted;
    const isGameActive = room.gameStatus === 'playing' && room.targetWord;

    return (
        <div className="min-h-screen bg-wordle-dark p-4">
            {/* Yayıncı modu kontrolleri */}
            {isStreamerMode && isHost && (
                <div className="streamer-controls">
                    <div className="text-white text-sm font-semibold mb-3">📺 Yayıncı Kontrolleri</div>
                    <div
                        className={`streamer-toggle ${showRoomCode ? 'active' : ''}`}
                        onClick={() => setShowRoomCode(!showRoomCode)}
                    >
                        <span>🏠</span>
                        <span>Oda Kodu</span>
                    </div>
                    <div
                        className={`streamer-toggle ${showTargetWord ? 'active' : ''}`}
                        onClick={() => setShowTargetWord(!showTargetWord)}
                    >
                        <span>📝</span>
                        <span>Kelime</span>
                    </div>
                    <div
                        className={`streamer-toggle ${showPlayerGuesses ? 'active' : ''}`}
                        onClick={() => setShowPlayerGuesses(!showPlayerGuesses)}
                    >
                        <span>👥</span>
                        <span>Oyuncu Tahminleri</span>
                    </div>
                </div>
            )}

            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
                    <div className="w-full sm:w-auto">
                        <h1 className="text-2xl sm:text-3xl font-bold text-white">Grup Wordle</h1>
                        <p className="text-gray-300 text-sm sm:text-base">
                            Oda Kodu: <span className={`font-mono text-wordle-green ${isStreamerMode && !showRoomCode ? 'streamer-blur' : ''}`}>{room.code}</span>
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                        {isHost && (
                            <button
                                onClick={restartGame}
                                className="btn out text-xs sm:text-sm"
                            >
                                Yeniden Başlat
                            </button>
                        )}
                        <button
                            onClick={copyRoomCode}
                            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-xs sm:text-sm"
                        >
                            Kodu Kopyala
                        </button>
                        <button
                            onClick={copyShareLink}
                            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-xs sm:text-sm"
                        >
                            Link Paylaş
                        </button>
                        <button
                            onClick={async () => {
                                // Host ise odayı sil ve oyunculara bildirim gönder
                                if (isHost && room) {
                                    try {
                                        await GameService.notifyRoomDeleted(room.code);
                                        await GameService.deleteRoom(room.code);
                                    } catch (error) {
                                        console.error('Oda silme hatası:', error);
                                    }
                                }

                                // Session'ı temizle
                                const sessionKey = `wordle_session_${roomId}`;
                                localStorage.removeItem(sessionKey);
                                navigate('/');
                            }}
                            className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-xs sm:text-sm"
                        >
                            Çıkış
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                    {/* Oyun alanı */}
                    <div className="lg:col-span-2">
                        {room.gameStatus === 'waiting' && (
                            <div className="text-center py-6 sm:py-12">
                                <h2 className="text-xl sm:text-2xl font-semibold text-white mb-4">
                                    {isHost ? 'Kelimeyi belirleyin' : 'Host kelimeyi belirliyor...'}
                                </h2>
                                <p className="text-gray-300 mb-6">
                                    Oda kodunu paylaşın: <span className={`font-mono text-wordle-green text-xl ${isStreamerMode && !showRoomCode ? 'streamer-blur' : ''}`}>{room.code}</span>
                                </p>
                                {isStreamerMode && (
                                    <div className="bg-blue-900 bg-opacity-30 border border-blue-500 rounded-lg p-3 mb-4">
                                        <p className="text-blue-200 text-sm">
                                            📺 Yayıncı Modu: Kelime yazarken yıldızlarla görünecek. Sağ üstteki kontrollerle bilgileri açabilirsiniz.
                                        </p>
                                    </div>
                                )}

                                {canSubmitWord && (
                                    <div className="max-w-md mx-auto">
                                        <div className="mb-4">
                                            <label className="block text-white text-sm font-medium mb-2">
                                                Kelime uzunluğu seçin:
                                            </label>
                                            <div className="flex gap-2 mb-4">
                                                {[4, 5, 6, 7, 8].map(length => (
                                                    <button
                                                        key={length}
                                                        onClick={() => setWordLength(length)}
                                                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${wordLength === length
                                                            ? 'bg-wordle-green text-white'
                                                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                                            }`}
                                                    >
                                                        {length} harf
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="mb-4">
                                            <label className="block text-white text-sm font-medium mb-2">
                                                {wordLength} harfli kelime girin:
                                                {isStreamerMode && (
                                                    <span className="text-blue-300 text-xs ml-2">(Yayıncı modu: yıldızlarla görünür)</span>
                                                )}
                                            </label>
                                            <input
                                                type={isStreamerMode ? "password" : "text"}
                                                value={hostWord}
                                                onChange={(e) => setHostWord(e.target.value.toUpperCase())}
                                                placeholder={`${wordLength} harfli kelime girin`}
                                                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-wordle-green focus:outline-none text-center text-xl font-mono"
                                                maxLength={wordLength}
                                            />
                                            {hostWord.length === wordLength && hostWordValid === false && (
                                                <p className="text-red-400 text-sm mt-2">
                                                    Geçerli bir kelime girin
                                                </p>
                                            )}
                                        </div>
                                        <button
                                            onClick={submitWord}
                                            disabled={hostWord.length !== wordLength || !hostWordValid}
                                            className="px-6 py-3 bg-wordle-green hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
                                        >
                                            Kelimeyi Gönder
                                        </button>
                                    </div>
                                )}

                                {!isHost && room.gameStatus === 'waiting' && (
                                    <p className="text-gray-400">
                                        Host kelimeyi belirleyene kadar bekleyin...
                                    </p>
                                )}
                            </div>
                        )}

                        {isGameActive && (
                            <div>
                                <div className="text-center mb-6">
                                    <h2 className="text-xl font-semibold text-white mb-2">
                                        {isHost ? 'Diğer oyuncular kelimeyi bulmaya çalışıyor...' : 'Kelimeyi bulun!'}
                                    </h2>
                                    <p className="text-gray-300">
                                        {(currentPlayer.guesses?.length || 0)}/{room.maxGuesses} tahmin
                                    </p>
                                </div>

                                {!isHost ? (
                                    <WordleGame
                                        targetWord={room.targetWord}
                                        currentGuess={currentGuess}
                                        onGuessChange={handleGuessChange}
                                        onGuessSubmit={handleGuessSubmit}
                                        guesses={currentPlayer.guesses || []}
                                        maxGuesses={room.maxGuesses}
                                        isGameComplete={currentPlayer.isGameComplete || false}
                                        hasWon={currentPlayer.hasWon || false}
                                        disabled={!isGameActive}
                                        wordLength={room.targetWord.length}
                                        pendingGuess={pendingGuess || undefined}
                                    />
                                ) : (
                                    <div className="text-center py-6 sm:py-12">
                                        <div className="bg-gray-800 rounded-lg p-4 sm:p-8">
                                            <h3 className="text-lg sm:text-xl font-semibold text-white mb-4">
                                                Belirlediğiniz Kelime:
                                            </h3>
                                            <div className={`bg-wordle-green text-white font-mono text-2xl sm:text-3xl py-3 sm:py-4 px-4 sm:px-6 rounded-lg mb-4 ${isStreamerMode && !showTargetWord ? 'streamer-blur' : ''}`}>
                                                {room.targetWord}
                                            </div>
                                            <p className="text-gray-300 text-sm sm:text-base mb-4">
                                                Diğer oyuncular bu kelimeyi bulmaya çalışıyor.
                                                Onların tahminlerini sağ taraftaki oyuncu listesinde görebilirsiniz.
                                            </p>

                                            {/* Host için oyuncu durumu özeti */}
                                            <div className="mt-6 p-4 bg-gray-700 rounded-lg">
                                                <h4 className="text-white font-semibold mb-3">Oyuncu Durumu:</h4>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                                    {room.players?.filter(p => !p.isHost).map(player => (
                                                        <div key={player.id} className="flex justify-between items-center">
                                                            <span className="text-gray-300">{player.name}:</span>
                                                            <span className={`font-medium ${player.hasWon ? 'text-wordle-green' :
                                                                player.isGameComplete ? 'text-red-400' :
                                                                    'text-wordle-yellow'
                                                                }`}>
                                                                {player.hasWon ? 'Kazandı' :
                                                                    player.isGameComplete ? 'Başarısız' :
                                                                        `${player.guesses?.length || 0} tahmin`}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {room.gameStatus === 'finished' && (
                            <div className="text-center py-6 sm:py-12">
                                <h2 className="text-xl sm:text-2xl font-semibold text-white mb-4">Oyun Bitti!</h2>
                                <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-6">
                                    <h3 className="text-lg font-semibold text-white mb-4">
                                        Kelime: <span className={`text-wordle-green font-mono ${isStreamerMode && !showTargetWord ? 'streamer-blur' : ''}`}>{room.targetWord}</span>
                                    </h3>
                                    <div className="space-y-2">
                                        {room.players?.sort((a, b) => {
                                            // Host her zaman en üstte
                                            if (a.isHost) return -1;
                                            if (b.isHost) return 1;

                                            // Kazananlar önce
                                            if (a.hasWon && !b.hasWon) return -1;
                                            if (!a.hasWon && b.hasWon) return 1;

                                            // İkisi de kazandıysa tahmin sayısına göre sırala
                                            if (a.hasWon && b.hasWon) {
                                                return (a.guesses?.length || 0) - (b.guesses?.length || 0);
                                            }

                                            // İkisi de kazanmadıysa tahmin sayısına göre sırala
                                            if (!a.hasWon && !b.hasWon) {
                                                return (a.guesses?.length || 0) - (b.guesses?.length || 0);
                                            }

                                            return 0;
                                        }).map(player => (
                                            <div key={player.id} className={`flex justify-between items-center p-2 sm:p-3 rounded-lg ${player.hasWon ? 'bg-wordle-green bg-opacity-20' : 'bg-gray-700'}`}>
                                                <span className="text-white font-medium text-sm sm:text-base">{player.name}</span>
                                                <span className={`font-semibold text-xs sm:text-sm ${player.hasWon ? 'text-wordle-green' : 'text-red-400'}`}>
                                                    {player.hasWon ? `🏆 ${player.guesses?.length || 0} tahminle kazandı` : '❌ Başarısız'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {isHost ? (
                                    <div className="space-y-4">
                                        <p className="text-gray-300 mb-4">Yeni oyun başlatmak için aşağıdaki butona tıklayın</p>
                                        <button
                                            onClick={restartGame}
                                            className="btn"
                                        >
                                            Yeni Oyun Başlat
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <p className="text-gray-300 mb-4">Host yeni oyun başlatana kadar bekleyin...</p>
                                        <div className="flex items-center justify-center space-x-2">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-wordle-green"></div>
                                            <span className="text-gray-300">Bekleniyor...</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Oyuncu listesi */}
                    <div className="max-h-64 sm:max-h-none overflow-y-auto">
                        <PlayerList
                            players={room.players || []}
                            currentPlayerId={currentPlayer.id}
                            isHost={isHost}
                            targetWord={room.targetWord}
                            isStreamerMode={isStreamerMode}
                            showPlayerGuesses={showPlayerGuesses}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GameRoom;
